import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';
import { downloadBlob } from '../../../shared/browser-compat';
import {
    BlockchainFailureReportService,
    FailureDay,
    FailureMonth,
    ReportScope
} from '../../../shared/services/blockchain-failure-report.service';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Toolbar button that downloads the blockchain upload failures as Excel.
 *
 * Covers every way a record can fail to reach the chain: the validation
 * failures the queue dead-letters and never retries (QR code mismatch, no QR
 * code in the document, private file URL), the ones the 02:00 recovery
 * scheduler retried and still could not register, files uploaded but never
 * submitted, and duplicate file URLs. Records that did reach the chain are
 * excluded, so what you download is only what is still failing.
 *
 * The menu lists recent days individually and folds older failures into
 * months — `unprocessed` records are dated by upload time and trail back
 * months over dozens of sparse days, so a plain day list left most of the
 * badge total with nowhere to appear.
 *
 * The workbook is generated in the browser — the API returns JSON only.
 */
@Component({
    selector: 'app-failure-report-download',
    templateUrl: './failure-report-download.component.html',
    styleUrls: ['./failure-report-download.component.scss']
})
export class FailureReportDownloadComponent implements OnInit, OnDestroy {
    days: FailureDay[] = [];
    months: FailureMonth[] = [];
    openTotal = 0;
    recoveredTotal = 0;
    recentDays = 7;
    loading = false;
    /** scopeKey of the row currently downloading, or null when idle. */
    downloading: string | null = null;
    canView = false;

    private destroy$ = new Subject<void>();

    constructor(
        private reportService: BlockchainFailureReportService,
        private authService: AuthService,
        private router: Router,
        private snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        // Same privilege that gates the audit-log export.
        this.canView = this.authService.hasPrivilege('system.export');
        if (this.canView) {
            this.loadSummary();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * The count shown in the pill. Rendered in full (thousands separated)
     * rather than abbreviated — the pill grows to fit, which is the whole
     * reason it replaced the fixed-width Material badge that clipped "99+"
     * down to a bare "+". Only an implausibly large number is capped.
     */
    get countText(): string {
        if (this.openTotal > 9999) return '9999+';
        return this.openTotal.toLocaleString();
    }

    get tooltip(): string {
        return this.openTotal > 0
            ? `Blockchain failure report — ${this.openTotal} record(s) failing`
            : 'Blockchain failure report — nothing failing';
    }

    get ariaLabel(): string {
        return this.openTotal > 0
            ? `Download blockchain failure report, ${this.openTotal} records failing`
            : 'Download blockchain failure report, no failures';
    }

    get hasBuckets(): boolean {
        return this.days.length > 0 || this.months.length > 0;
    }

    /** Refresh on menu open, so the morning's recovery run shows up. */
    loadSummary(): void {
        this.loading = true;
        this.reportService.getSummary(this.recentDays)
            .pipe(takeUntil(this.destroy$))
            .subscribe(summary => {
                this.openTotal = summary.openTotal;
                this.recoveredTotal = summary.recoveredTotal;
                this.recentDays = summary.recentDays;
                this.days = summary.days;
                this.months = summary.months;
                this.loading = false;
            });
    }

    allScope(): ReportScope {
        return { kind: 'all' };
    }

    dayScope(day: FailureDay): ReportScope {
        return { kind: 'day', date: day.date };
    }

    monthScope(month: FailureMonth): ReportScope {
        return { kind: 'month', month: month.month, from: month.from, to: month.to };
    }

    key(scope: ReportScope): string {
        return this.reportService.scopeKey(scope);
    }

    /**
     * Open the full page. The current date range travels in the query string,
     * so the page opens on what the user was already looking at rather than
     * resetting to a default window.
     */
    openFullReport(scope?: ReportScope): void {
        const queryParams: { [k: string]: string } = {};
        if (scope && scope.kind === 'day') {
            queryParams['from'] = scope.date;
            queryParams['to'] = scope.date;
        } else if (scope && (scope.kind === 'month' || scope.kind === 'range')) {
            queryParams['from'] = scope.from;
            queryParams['to'] = scope.to;
        } else if (scope && scope.kind === 'all') {
            queryParams['all'] = 'true';
        }
        this.router.navigate(['/dms/blockchain-failures'], { queryParams });
    }

    download(scope: ReportScope): void {
        if (this.downloading) return;
        const key = this.reportService.scopeKey(scope);
        this.downloading = key;

        this.reportService.getReport(scope)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (report) => {
                    this.downloading = null;
                    if (!report.total && !report.recoveredCount) {
                        this.snackBar.open('Nothing to report for that selection', 'Close', { duration: 3000 });
                        return;
                    }
                    const blob = this.reportService.buildWorkbook(report);
                    downloadBlob(blob, this.reportService.fileNameFor(scope));
                    const parts = [`${report.total} failed`];
                    if (report.recoveredCount) parts.push(`${report.recoveredCount} recovered`);
                    this.snackBar.open(
                        `Downloaded ${parts.join(', ')} record(s)`,
                        'Close',
                        { duration: 3000 }
                    );
                },
                error: () => {
                    this.downloading = null;
                    this.snackBar.open('Failed to build the failure report', 'Close', { duration: 4000 });
                }
            });
    }

    /** "3 Sep 2026" — short enough for a menu row. */
    formatDate(date: string): string {
        if (!date) return '';
        // A plain YYYY-MM-DD: split it rather than letting Date parse-and-shift
        // it across a timezone boundary.
        const [y, m, d] = date.split('-').map(Number);
        if (!y || !m || !d) return date;
        return `${d} ${MONTH_SHORT[m - 1]} ${y}`;
    }

    /** "March 2026" for a YYYY-MM month bucket. */
    formatMonth(month: string): string {
        if (!month) return '';
        const [y, m] = month.split('-').map(Number);
        if (!y || !m) return month;
        return `${MONTH_NAMES[m - 1]} ${y}`;
    }

    isToday(date: string): boolean {
        const now = new Date();
        const p = (n: number) => String(n).padStart(2, '0');
        return date === `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
    }
}
